import { useCallback, useEffect, useRef, useState } from 'react';

/** What the assistant is shown to be doing while it has not written yet. */
export type AssistantStatus =
  | { phase: 'thinking' }
  | { phase: 'consulting'; tool: string }
  | { phase: 'analysing' };

const THINKING: AssistantStatus = { phase: 'thinking' };
const ANALYSING: AssistantStatus = { phase: 'analysing' };

/** How long a status is shown before the next one takes its place. The tools are done much sooner than that. */
const MIN_SHOWN_MS = 1000;

const isSame = (a: AssistantStatus, b: AssistantStatus) =>
  a.phase === 'consulting' && b.phase === 'consulting'
    ? a.tool === b.tool
    : a.phase === b.phase;

/**
 * What to show of what the assistant does, from what happens to it: a tool starts, finishes or fails, and
 * the answer starts and ends. Every status is shown for at least a second, in the order it came, so that the user
 * can see it; one that comes while another is shown waits its turn. It is only for the eye: the text of
 * the answer takes the place of all of it at once.
 */
const useAssistantStatus = () => {
  const [status, setStatus] = useState<AssistantStatus>(THINKING);
  const waiting = useRef<AssistantStatus[]>([]);
  const current = useRef<AssistantStatus>(THINKING);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showNext = useCallback(() => {
    const next = waiting.current.shift();
    if (!next) {
      // What is shown stays until something else comes, which is then shown right away.
      timer.current = undefined;
      return;
    }

    current.current = next;
    setStatus(next);
    timer.current = setTimeout(showNext, MIN_SHOWN_MS);
  }, []);

  const show = useCallback(
    (next: AssistantStatus) => {
      const last = waiting.current.at(-1) ?? current.current;
      if (isSame(next, last)) {
        return;
      }

      waiting.current.push(next);
      if (!timer.current) {
        showNext();
      }
    },
    [showNext]
  );

  const reset = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = undefined;
    waiting.current = [];
    current.current = THINKING;
    setStatus(THINKING);
  }, []);

  /** A tool that starts is being consulted. */
  const toolStarted = useCallback(
    (tool: string) => show({ phase: 'consulting', tool }),
    [show]
  );

  /** What a tool gave is being analysed. */
  const toolFinished = useCallback(() => show(ANALYSING), [show]);

  /** A tool that failed leaves nothing to look at, but the assistant goes on, so it is analysing too. */
  const toolFailed = useCallback(() => show(ANALYSING), [show]);

  /** The text takes the place of all of it, at once. */
  const answerStarted = useCallback(() => reset(), [reset]);

  /** The answer is over, written or failed, and the next one starts from thinking. */
  const answerEnded = useCallback(() => reset(), [reset]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return {
    status,
    toolStarted,
    toolFinished,
    toolFailed,
    answerStarted,
    answerEnded,
  };
};

export default useAssistantStatus;
